import React, { useMemo, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import {
  Check,
  Film,
  Globe2,
  Loader2,
  Sparkles,
  WandSparkles,
  X,
} from "lucide-react";
import {
  PROJECT_PRODUCTION_MODE_INTERACTIVE_GAME,
  PROJECT_PRODUCTION_MODE_NARRATION,
  PROJECT_PRODUCTION_MODE_REPAINT,
  normalizeProjectProductionMode,
} from "../../utils/projectRuntime";
import {
  PROJECT_TYPE_OPTIONS,
  getProjectTypeSelection,
} from "../../utils/projectTypeSettings";

const SPECIALIZED_PRODUCTION_MODES = new Set([
  PROJECT_PRODUCTION_MODE_NARRATION,
  PROJECT_PRODUCTION_MODE_REPAINT,
  PROJECT_PRODUCTION_MODE_INTERACTIVE_GAME,
]);

const RADIO_KEY_DIRECTIONS = Object.freeze({
  ArrowDown: 1,
  ArrowRight: 1,
  ArrowUp: -1,
  ArrowLeft: -1,
});

const optionIcon = (option) => {
  if (option.key === "wan3") return WandSparkles;
  if (option.key === "sd25") return Sparkles;
  if (option.key.startsWith("overseas_")) return Globe2;
  return Film;
};

const optionAccent = (option) => option.accent || "var(--color-accent-primary)";

const productionModeLabel = (mode) => {
  if (mode === PROJECT_PRODUCTION_MODE_NARRATION) return "解说剧生产";
  if (mode === PROJECT_PRODUCTION_MODE_REPAINT) return "影视转绘";
  if (mode === PROJECT_PRODUCTION_MODE_INTERACTIVE_GAME) return "互动游戏";
  return "专用工作流";
};

const ProjectTypeSettingsModal = ({ project, onClose, onSave }) => {
  const currentProductionMode = normalizeProjectProductionMode(project);
  const specializedMode = SPECIALIZED_PRODUCTION_MODES.has(currentProductionMode);
  const currentSelection = useMemo(() => getProjectTypeSelection(project), [project]);
  const [selectedKey, setSelectedKey] = useState(() => (specializedMode ? "" : currentSelection));
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const selectedOption = PROJECT_TYPE_OPTIONS.find((option) => option.key === selectedKey) || null;
  const changed = Boolean(selectedOption) && (specializedMode || selectedKey !== currentSelection);

  const selectOption = (key) => {
    setSelectedKey(key);
    setErrorMessage("");
  };

  const handleOptionKeyDown = (event, index) => {
    const direction = RADIO_KEY_DIRECTIONS[event.key];
    let nextIndex = null;
    if (direction) {
      nextIndex = (index + direction + PROJECT_TYPE_OPTIONS.length) % PROJECT_TYPE_OPTIONS.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = PROJECT_TYPE_OPTIONS.length - 1;
    }
    if (nextIndex == null) return;
    event.preventDefault();
    const nextOption = PROJECT_TYPE_OPTIONS[nextIndex];
    selectOption(nextOption.key);
    document.getElementById(`project-type-option-${nextOption.key}`)?.focus();
  };

  const handleSave = async () => {
    if (!changed || isSaving) return;
    setIsSaving(true);
    setErrorMessage("");
    try {
      await onSave(selectedKey);
    } catch (error) {
      setErrorMessage(error?.message || String(error || "项目类型保存失败"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={isSaving ? () => {} : onClose}
      className="relative z-[110]"
    >
      <DialogBackdrop
        transition
        className="ui-dialog-backdrop fixed inset-0 backdrop-blur-sm"
        style={{ backgroundColor: "var(--overlay-medium)" }}
      />
      <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
        <div className="flex min-h-full items-center justify-center">
          <DialogPanel
            transition
            className="ui-dialog-panel relative flex max-h-[min(760px,calc(100vh-32px))] w-full max-w-3xl flex-col overflow-hidden rounded-2xl shadow-2xl"
            style={{
              backgroundColor: "var(--color-bg-secondary)",
              border: "1px solid var(--color-border-primary)",
              boxShadow: "var(--shadow-modal)",
            }}
          >
            <div
              className="flex items-start justify-between gap-4 border-b px-5 py-4 sm:px-6"
              style={{ borderColor: "var(--color-border-primary)" }}
            >
              <div className="min-w-0">
                <DialogTitle className="text-lg font-bold" style={{ color: "var(--color-text-primary)" }}>
                  修改项目类型
                </DialogTitle>
                <p className="mt-1 text-xs leading-5" style={{ color: "var(--color-text-muted)" }}>
                  修改后，后续分镜规则、视频线路和参考素材传输方式会跟随新类型；已有剧本、资产和故事板不会删除。
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="ui-pressable flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-xl transition-colors hover:bg-[var(--color-bg-tertiary)] disabled:cursor-not-allowed disabled:opacity-50"
                style={{ color: "var(--color-text-muted)" }}
                aria-label="关闭修改项目类型弹窗"
                title="关闭"
              >
                <X size={19} />
              </button>
            </div>

            <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
              {specializedMode && (
                <div
                  className="mb-4 rounded-xl border px-4 py-3 text-xs leading-5"
                  style={{
                    borderColor: "color-mix(in srgb, var(--color-warning, #f59e0b) 35%, transparent)",
                    backgroundColor: "color-mix(in srgb, var(--color-warning, #f59e0b) 8%, transparent)",
                    color: "var(--color-text-secondary)",
                  }}
                >
                  当前项目使用“{productionModeLabel(currentProductionMode)}”专用流程。选择下面任一类型并保存后，将切换为对应的视频项目流程。
                </div>
              )}

              <div role="radiogroup" aria-label="项目类型" className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {PROJECT_TYPE_OPTIONS.map((option, index) => {
                  const selected = option.key === selectedKey;
                  const Icon = optionIcon(option);
                  const accent = optionAccent(option);
                  return (
                    <button
                      key={option.key}
                      id={`project-type-option-${option.key}`}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      autoFocus={selected}
                      onClick={() => selectOption(option.key)}
                      onKeyDown={(event) => handleOptionKeyDown(event, index)}
                      className="ui-pressable min-h-[112px] cursor-pointer rounded-xl border p-4 text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-secondary)]"
                      style={{
                        borderColor: selected ? accent : "var(--color-border-primary)",
                        backgroundColor: selected
                          ? `color-mix(in srgb, ${accent} 9%, var(--color-bg-overlay))`
                          : "var(--color-bg-overlay)",
                      }}
                    >
                      <span className="flex items-start gap-3">
                        <span
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border"
                          style={{
                            color: accent,
                            borderColor: `color-mix(in srgb, ${accent} 35%, transparent)`,
                            backgroundColor: `color-mix(in srgb, ${accent} 10%, transparent)`,
                          }}
                        >
                          <Icon size={17} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>
                              {option.title}
                            </span>
                            {selected && <Check size={15} className="shrink-0" style={{ color: accent }} />}
                          </span>
                          <span className="mt-0.5 block text-xs font-semibold" style={{ color: accent }}>
                            {option.providerLabel}
                          </span>
                          <span className="mt-1.5 block text-xs leading-5" style={{ color: "var(--color-text-muted)" }}>
                            {option.description}
                          </span>
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 min-h-5" aria-live="polite">
                {errorMessage && (
                  <p className="text-xs" style={{ color: "var(--color-error)" }}>
                    保存失败：{errorMessage}
                  </p>
                )}
              </div>
            </div>

            <div
              className="flex flex-col-reverse gap-2 border-t px-5 py-4 sm:flex-row sm:items-center sm:justify-end sm:px-6"
              style={{ borderColor: "var(--color-border-primary)" }}
            >
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="ui-pressable min-h-11 cursor-pointer rounded-xl px-5 text-sm font-semibold transition-colors hover:bg-[var(--color-bg-tertiary)] disabled:cursor-not-allowed disabled:opacity-50"
                style={{ color: "var(--color-text-secondary)" }}
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!changed || isSaving}
                className="ui-pressable inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-xl px-6 text-sm font-bold transition-opacity disabled:cursor-not-allowed disabled:opacity-45"
                style={{ backgroundColor: "var(--color-accent-primary)", color: "var(--color-accent-contrast)" }}
              >
                {isSaving && <Loader2 size={16} className="animate-spin" />}
                {isSaving ? "保存中…" : "保存项目类型"}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default ProjectTypeSettingsModal;
